import { jsPDF } from 'jspdf';
import 'svg2pdf.js';
import { DateFormatPattern, Logo, PdfExportSettings, PdfOrientation, PdfPaperSize, SynthesisGroup, WeekNumbering } from '../api/types';
import { api } from '../api/client';
import { addDaysUTC, computeDepthById, diffDaysUTC, parseISODate } from './ganttGeometry';
import { mergeCellStyle } from './cellStyle';
import { escapeXml, truncateToWidth } from './svgText';
import { planDateRange } from './planDateRange';
import {
  BORDER,
  FONT_STACK,
  GROUP_COLOR,
  PRIMARY,
  TEXT_DEFAULT,
  TEXT_MUTED,
  TODAY_COLOR,
  computeSpans,
  lineEl,
  rectEl,
  textEl,
} from './pdfPageSvg';
import {
  BAR_HEIGHT,
  COLUMN_WIDTH,
  COMPILED_COLUMNS,
  CompiledRow,
  CompiledRowLayout,
  DAY_ROW_HEIGHT,
  DAY_WIDTH,
  HEADER_HEIGHT,
  INDENT_PX,
  LABEL_WIDTH,
  MONTH_ROW_HEIGHT,
  NUM_COL_WIDTH,
  ROW_HEIGHT,
  WEEK_ROW_HEIGHT,
  buildCompiledRows,
  compiledColumnText,
  computeCompiledRowLayout,
} from './compiledChartLayout';
import { orientedPaperSizeMm, PAGE_HEADER_BAND_MM, PAGE_HEADER_GAP_MM, PX_TO_MM, LOGO_SIZE_MM } from './pdfLayout';
import { buildLogoSvgMarkup } from './pdfLogoSvg';
import { rasterizeLogoToPngDataUri } from './rasterizeLogo';
import { weekNumber } from './weekNumber';

/**
 * Export PDF du diagramme COMPILÉ (synthèse automatique ou charge de
 * travail — voir CompiledSynthesisChart.tsx et ARCHITECTURE.md) : réutilise
 * le même moteur bas niveau que l'export par diagramme source
 * (jsPDF + svg2pdf.js, textEl/rectEl/lineEl/computeSpans partagés avec
 * pdfPageSvg.ts, logos vectoriels via pdfLogoSvg.ts avec repli raster) mais
 * une géométrie propre : pas de diagramId unique à qui rattacher des
 * réglages persistés (plusieurs diagrammes source fusionnés), pas de
 * connecteurs/chemin critique (sans signification en travers de plannings
 * indépendants — voir CompiledSynthesisChart), des bandeaux séparateurs de
 * hauteur différente des lignes de tâche (pagination verticale par
 * "empilement" plutôt que par simple division, voir packRowsIntoPages).
 *
 * Réglages NON persistés côté serveur (voir PdfExportSettingsModal en mode
 * `onConfirm`) : ils vivent seulement le temps de la session, réinitialisés
 * à chaque ouverture de la modale — choix délibéré pour ne pas construire un
 * nouveau concept de stockage (par dossier+type de synthèse, ou par
 * dossier+rôle+utilisateur) pour un réglage qui n'est pas rattaché à un
 * Diagram réel.
 */

// Le compilé n'a jamais de connecteurs (voir docstring de CompiledSynthesisChart) : cette valeur n'est
// jamais lue par le rendu, présente seulement parce que le type Required<PdfExportSettings> l'exige.
const CONNECTOR_STYLE_IRRELEVANT = 'curved' as const;

export const COMPILED_DEFAULT_SETTINGS: Required<PdfExportSettings> = {
  columns: [...COMPILED_COLUMNS],
  connectorStyle: CONNECTOR_STYLE_IRRELEVANT,
  logoIds: [],
  // false, contrairement au défaut par diagramme source (true) : CompiledSynthesisChart affiche déjà
  // les tâches non visibles à l'identique des autres à l'écran (choix explicite de l'utilisateur, voir
  // ARCHITECTURE.md) — l'export doit refléter la même chose par défaut.
  onlyVisibleRows: false,
  dateRangeStart: null,
  dateRangeEnd: null,
  paperSize: 'A4',
  orientation: 'portrait',
  marginCm: 0.64,
  scalePercent: 100,
  fitToOnePage: false,
};

function sanitizeFilename(name: string): string {
  return name.replace(/[\\/:*?"<>|]/g, '_').trim() || 'export';
}

async function renderSvgString(doc: jsPDF, markup: string, opts: { x: number; y: number; width: number; height: number }): Promise<void> {
  const parsed = new DOMParser().parseFromString(markup, 'image/svg+xml');
  const el = parsed.documentElement;
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-99999px';
  container.style.top = '0';
  container.appendChild(el);
  document.body.appendChild(container);
  try {
    await doc.svg(el as unknown as HTMLElement, opts);
  } finally {
    document.body.removeChild(container);
  }
}

interface CompiledBarInfo {
  isMilestone: boolean;
  hasDates: boolean;
  barLeft: number;
  barWidth: number;
}

function computeCompiledBars(rows: CompiledRow[], rangeStart: Date): (CompiledBarInfo | null)[] {
  return rows.map((row) => {
    if (row.kind !== 'task') return null;
    const task = row.task;
    if (!task.startDate || !task.endDate) return { isMilestone: false, hasDates: false, barLeft: 0, barWidth: 0 };
    const start = parseISODate(task.startDate);
    const end = parseISODate(task.endDate);
    const isMilestone = task.startDate.slice(0, 10) === task.endDate.slice(0, 10);
    const barLeft = diffDaysUTC(rangeStart, start) * DAY_WIDTH;
    const barWidth = (diffDaysUTC(start, end) + 1) * DAY_WIDTH;
    return { isMilestone, hasDates: true, barLeft, barWidth };
  });
}

/** Découpe la liste de lignes (hauteur variable : bandeau séparateur vs tâche) en pages, sans jamais couper une ligne — accumulation, pas une simple division comme pour l'export par diagramme source. */
function packRowsIntoPages(rowLayout: CompiledRowLayout[], maxHeightPx: number): { startIndex: number; endIndex: number }[] {
  if (rowLayout.length === 0) return [{ startIndex: 0, endIndex: 0 }];
  const pages: { startIndex: number; endIndex: number }[] = [];
  let pageStart = 0;
  let pageStartTop = rowLayout[0].top;
  for (let i = 0; i < rowLayout.length; i++) {
    const rowBottom = rowLayout[i].top + rowLayout[i].height - pageStartTop;
    if (rowBottom > maxHeightPx && i > pageStart) {
      pages.push({ startIndex: pageStart, endIndex: i });
      pageStart = i;
      pageStartTop = rowLayout[i].top;
    }
  }
  pages.push({ startIndex: pageStart, endIndex: rowLayout.length });
  return pages;
}

export interface CompiledPagePlan {
  pagesWide: number;
  pagesTall: number;
  daysOnFirstCol: number;
  daysPerLaterCol: number;
  marginMm: number;
  usableWmm: number;
  usableHmm: number;
  contentUsableWpx: number;
  contentUsableHpx: number;
  rowPages: { startIndex: number; endIndex: number }[];
}

export function computeCompiledPagePlan(input: {
  rowLayout: CompiledRowLayout[];
  dayCount: number;
  paperSize: PdfPaperSize;
  orientation: PdfOrientation;
  marginCm: number;
  scalePercent: number;
}): CompiledPagePlan {
  const paper = orientedPaperSizeMm(input.paperSize, input.orientation);
  const marginMm = input.marginCm * 10;
  const usableWmm = Math.max(paper.w - marginMm * 2, 10);
  const usableHmm = Math.max(paper.h - marginMm * 2, 10);
  const scale = input.scalePercent / 100;

  const contentUsableHmm = Math.max(usableHmm - PAGE_HEADER_BAND_MM - PAGE_HEADER_GAP_MM, 10);
  const contentUsableWpx = usableWmm / (PX_TO_MM * scale);
  const contentUsableHpx = contentUsableHmm / (PX_TO_MM * scale);

  const dayCount = Math.max(input.dayCount, 1);
  const daysOnFirstCol = Math.max(0, Math.floor((contentUsableWpx - LABEL_WIDTH) / DAY_WIDTH));
  const daysPerLaterCol = Math.max(1, Math.floor(contentUsableWpx / DAY_WIDTH));
  const remainingDays = Math.max(0, dayCount - daysOnFirstCol);
  const pagesWide = daysOnFirstCol >= dayCount ? 1 : 1 + Math.ceil(remainingDays / daysPerLaterCol);

  const rowsAvailablePx = Math.max(contentUsableHpx - HEADER_HEIGHT, ROW_HEIGHT);
  const rowPages = packRowsIntoPages(input.rowLayout, rowsAvailablePx);

  return {
    pagesWide,
    pagesTall: rowPages.length,
    daysOnFirstCol,
    daysPerLaterCol,
    marginMm,
    usableWmm,
    usableHmm,
    contentUsableWpx,
    contentUsableHpx,
    rowPages,
  };
}

/** Pourcentage d'échelle (entier, borné à [10,400]) qui ferait tenir tout le diagramme compilé sur UNE page, en largeur ET en hauteur — équivalent de scaleToFitOnePage (pdfLayout.ts) pour la géométrie du compilé. */
export function compiledScaleToFitOnePage(rowLayout: CompiledRowLayout[], dayCount: number, paperSize: PdfPaperSize, orientation: PdfOrientation, marginCm: number): number {
  const paper = orientedPaperSizeMm(paperSize, orientation);
  const marginMm = marginCm * 10;
  const usableWmm = Math.max(paper.w - marginMm * 2, 10);
  const usableHmm = Math.max(paper.h - marginMm * 2, 10);
  const contentUsableHmm = Math.max(usableHmm - PAGE_HEADER_BAND_MM - PAGE_HEADER_GAP_MM, 10);

  const totalRowsHeightPx = rowLayout.length ? rowLayout[rowLayout.length - 1].top + rowLayout[rowLayout.length - 1].height : 0;
  const requiredWpx = LABEL_WIDTH + Math.max(dayCount, 1) * DAY_WIDTH;
  const requiredHpx = HEADER_HEIGHT + totalRowsHeightPx;

  const scaleForW = (usableWmm / (requiredWpx * PX_TO_MM)) * 100;
  const scaleForH = (contentUsableHmm / (requiredHpx * PX_TO_MM)) * 100;
  return Math.max(10, Math.min(400, Math.floor(Math.min(scaleForW, scaleForH))));
}

interface CompiledPageContentParams {
  rows: CompiledRow[];
  rowLayoutFull: CompiledRowLayout[];
  bars: (CompiledBarInfo | null)[];
  depthById: Map<string, number>;
  includeLabelCol: boolean;
  dateFormat: DateFormatPattern;
  weekNumbering: WeekNumbering;
  rangeStart: Date;
  dayStart: number;
  dayCount: number;
  rowStart: number;
  rowEnd: number;
  todayDayIndex: number | null;
  weekAbbrev: (n: number) => string;
  columnLabel: (col: string) => string;
  locale: string;
}

function buildCompiledPageContentSvg(p: CompiledPageContentParams): { svg: string; widthPx: number; heightPx: number } {
  const timelineWidth = p.dayCount * DAY_WIDTH;
  const contentWidth = (p.includeLabelCol ? LABEL_WIDTH : 0) + timelineWidth;
  const timelineX0 = p.includeLabelCol ? LABEL_WIDTH : 0;
  const pageTop0 = p.rowLayoutFull[p.rowStart]?.top ?? 0;
  const rowsHeight = p.rowEnd > p.rowStart ? p.rowLayoutFull[p.rowEnd - 1].top + p.rowLayoutFull[p.rowEnd - 1].height - pageTop0 : 0;
  const contentHeight = HEADER_HEIGHT + rowsHeight;

  const parts: string[] = [];

  // --- Tableau (5 colonnes fixes), uniquement sur la 1re colonne de pages ---
  if (p.includeLabelCol) {
    let colX = NUM_COL_WIDTH;
    parts.push(rectEl(0, 0, LABEL_WIDTH, HEADER_HEIGHT, { fill: '#ffffff', stroke: BORDER }));
    for (const col of COMPILED_COLUMNS) {
      const w = COLUMN_WIDTH[col];
      parts.push(textEl(colX + 2, HEADER_HEIGHT - 6, p.columnLabel(col), { fontSize: 9, fontWeight: 'bold' }));
      colX += w;
    }
    for (let i = p.rowStart; i < p.rowEnd; i++) {
      const row = p.rows[i];
      const localTop = HEADER_HEIGHT + (p.rowLayoutFull[i].top - pageTop0);
      const height = p.rowLayoutFull[i].height;
      if (row.kind === 'separator') {
        parts.push(textEl(8, localTop + height / 2 + 4, row.assigneeName ? `${row.diagramName} — ${row.assigneeName}` : row.diagramName, { fontSize: 12, fontWeight: 'bold' }));
        continue;
      }
      const task = row.task;
      if (task.formatting?.row?.backgroundColor) parts.push(rectEl(0, localTop, LABEL_WIDTH, height, { fill: task.formatting.row.backgroundColor }));
      parts.push(textEl(4, localTop + height / 2 + 4, String(task.number), { fontSize: 10, fill: TEXT_MUTED }));

      let cx = NUM_COL_WIDTH;
      for (const col of COMPILED_COLUMNS) {
        const w = COLUMN_WIDTH[col];
        const style = mergeCellStyle(task.formatting, col);
        if (style.backgroundColor) parts.push(rectEl(cx, localTop, w, height, { fill: style.backgroundColor }));
        const indent = col === COMPILED_COLUMNS[0] ? (p.depthById.get(task.id) ?? 0) * INDENT_PX : 0;
        const raw = compiledColumnText(task, col, p.dateFormat);
        const text = truncateToWidth(raw, w - 6 - indent, `${style.bold ? 'bold ' : ''}${style.fontSize ?? 11}px ${style.fontFamily || FONT_STACK}`);
        const decorations: string[] = [];
        if (style.underline) decorations.push('underline');
        if (style.strikethrough) decorations.push('line-through');
        if (style.highlightColor) parts.push(rectEl(cx + 2 + indent, localTop + height - 6, Math.max(w - 4 - indent, 0), 3, { fill: style.highlightColor }));
        parts.push(
          textEl(cx + 3 + indent, localTop + height / 2 + 4, text, {
            fontSize: style.fontSize ?? 11,
            fontWeight: style.bold ? 'bold' : undefined,
            fontStyle: style.italic ? 'italic' : undefined,
            fill: style.color ?? TEXT_DEFAULT,
            fontFamily: style.fontFamily,
            decoration: decorations.length ? decorations.join(' ') : undefined,
          }),
        );
        cx += w;
      }
      parts.push(lineEl(0, localTop + height, LABEL_WIDTH, localTop + height, { stroke: BORDER }));
    }
    parts.push(lineEl(LABEL_WIDTH, 0, LABEL_WIDTH, contentHeight, { stroke: BORDER }));
  }

  // --- En-tête frise (mois / semaine / jour) ---
  const days: Date[] = [];
  for (let i = 0; i < p.dayCount; i++) days.push(addDaysUTC(p.rangeStart, p.dayStart + i));
  const monthSpans = computeSpans(
    days,
    (d) => `${d.getUTCFullYear()}-${d.getUTCMonth()}`,
    (d) => d.toLocaleDateString(p.locale, { month: 'long', year: 'numeric' }),
  );
  const weekSpans = computeSpans(
    days,
    (d) => String(weekNumber(d, p.weekNumbering)),
    (d) => p.weekAbbrev(weekNumber(d, p.weekNumbering)),
  );

  parts.push(rectEl(timelineX0, 0, timelineWidth, HEADER_HEIGHT, { fill: '#ffffff', stroke: BORDER }));
  for (const span of monthSpans) {
    parts.push(lineEl(timelineX0 + span.left, 0, timelineX0 + span.left, MONTH_ROW_HEIGHT, { stroke: BORDER }));
    parts.push(textEl(timelineX0 + span.left + span.width / 2, MONTH_ROW_HEIGHT - 5, span.label, { fontSize: 10, fontWeight: 'bold', anchor: 'middle' }));
  }
  for (const span of weekSpans) {
    const y0 = MONTH_ROW_HEIGHT;
    parts.push(lineEl(timelineX0 + span.left, y0, timelineX0 + span.left, y0 + WEEK_ROW_HEIGHT, { stroke: BORDER }));
    parts.push(textEl(timelineX0 + span.left + span.width / 2, y0 + WEEK_ROW_HEIGHT - 4, span.label, { fontSize: 9, fill: TEXT_MUTED, anchor: 'middle' }));
  }
  days.forEach((d, i) => {
    const x = timelineX0 + i * DAY_WIDTH;
    const y0 = MONTH_ROW_HEIGHT + WEEK_ROW_HEIGHT;
    if (d.getUTCDay() === 1) parts.push(lineEl(x, y0, x, y0 + DAY_ROW_HEIGHT, { stroke: BORDER }));
    parts.push(textEl(x + DAY_WIDTH / 2, y0 + DAY_ROW_HEIGHT - 5, String(d.getUTCDate()), { fontSize: 9, fill: TEXT_MUTED, anchor: 'middle' }));
  });
  parts.push(lineEl(timelineX0, HEADER_HEIGHT, timelineX0 + timelineWidth, HEADER_HEIGHT, { stroke: BORDER }));

  // --- Bandeaux/grille + barres ---
  const dayWindowStart = p.dayStart * DAY_WIDTH;
  const dayWindowEnd = (p.dayStart + p.dayCount) * DAY_WIDTH;
  for (let i = p.rowStart; i < p.rowEnd; i++) {
    const row = p.rows[i];
    const localTop = HEADER_HEIGHT + (p.rowLayoutFull[i].top - pageTop0);
    const height = p.rowLayoutFull[i].height;
    if (row.kind === 'separator') continue; // aucun bandeau/bordure dans la frise pour un séparateur, comme à l'écran.
    parts.push(lineEl(timelineX0, localTop + height, timelineX0 + timelineWidth, localTop + height, { stroke: BORDER }));

    const bar = p.bars[i];
    if (!bar || !bar.hasDates) continue;
    const task = row.task;
    const barColor = task.isGroup ? GROUP_COLOR : PRIMARY;
    if (bar.isMilestone) {
      const cx = bar.barLeft + DAY_WIDTH / 2;
      if (cx < dayWindowStart - 10 || cx > dayWindowEnd + 10) continue;
      const localCx = timelineX0 + cx - dayWindowStart;
      const localCy = localTop + height / 2;
      const half = 7;
      const points = [
        [localCx, localCy - half],
        [localCx + half, localCy],
        [localCx, localCy + half],
        [localCx - half, localCy],
      ]
        .map((pt) => pt.join(','))
        .join(' ');
      parts.push(`<polygon points="${points}" fill="${barColor}"/>`);
      continue;
    }
    const clampedLeft = Math.max(bar.barLeft, dayWindowStart);
    const clampedRight = Math.min(bar.barLeft + bar.barWidth, dayWindowEnd);
    const clampedWidth = clampedRight - clampedLeft;
    if (clampedWidth <= 0) continue;
    const localLeft = timelineX0 + clampedLeft - dayWindowStart;
    const barTop = localTop + height / 2 - BAR_HEIGHT / 2;
    parts.push(rectEl(localLeft, barTop, clampedWidth, BAR_HEIGHT, { fill: barColor, rx: 3 }));
    if (!task.isGroup && task.progressPct > 0) {
      const progressGlobalEnd = bar.barLeft + (bar.barWidth * task.progressPct) / 100;
      const visibleProgressWidth = Math.min(progressGlobalEnd, clampedRight) - clampedLeft;
      if (visibleProgressWidth > 0) parts.push(rectEl(localLeft, barTop, visibleProgressWidth, BAR_HEIGHT, { fill: 'rgba(0,0,0,0.25)' }));
    }
  }

  // --- Ligne "aujourd'hui" ---
  if (p.todayDayIndex !== null && p.todayDayIndex >= p.dayStart && p.todayDayIndex < p.dayStart + p.dayCount) {
    const x = timelineX0 + (p.todayDayIndex - p.dayStart) * DAY_WIDTH;
    parts.push(lineEl(x, 0, x, contentHeight, { stroke: TODAY_COLOR, strokeWidth: 1 }));
  }

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${contentWidth} ${contentHeight}" width="${contentWidth}" height="${contentHeight}">${parts.join('')}</svg>`;
  return { svg, widthPx: contentWidth, heightPx: contentHeight };
}

export interface GenerateCompiledPdfParams {
  groups: SynthesisGroup[];
  title: string;
  settings: Required<PdfExportSettings>;
  dateFormat: DateFormatPattern;
  weekNumbering: WeekNumbering;
  locale: string;
  weekAbbrev: (n: number) => string;
  columnLabel: (col: string) => string;
}

export async function generateCompiledSynthesisPdf({
  groups,
  title,
  settings,
  dateFormat,
  weekNumbering,
  locale,
  weekAbbrev,
  columnLabel,
}: GenerateCompiledPdfParams): Promise<void> {
  const rowFilteredGroups = settings.onlyVisibleRows ? groups.map((g) => ({ ...g, tasks: g.tasks.filter((t) => t.visible) })) : groups;

  const allTasks = rowFilteredGroups.flatMap((g) => g.tasks);
  const defaultRange = planDateRange(allTasks);

  const startIso = settings.dateRangeStart ? settings.dateRangeStart.slice(0, 10) : defaultRange?.start;
  const endIso = settings.dateRangeEnd ? settings.dateRangeEnd.slice(0, 10) : defaultRange?.end;
  const rangeStart = startIso ? parseISODate(startIso) : new Date();
  const rangeEnd = endIso ? parseISODate(endIso) : addDaysUTC(rangeStart, 29);
  const totalDays = Math.max(1, diffDaysUTC(rangeStart, rangeEnd) + 1);

  const rangeStartIso = rangeStart.toISOString().slice(0, 10);
  const rangeEndIso = rangeEnd.toISOString().slice(0, 10);
  const dateFilteredGroups = rowFilteredGroups.map((g) => ({
    ...g,
    tasks: g.tasks.filter((task) => !task.startDate || !task.endDate || (task.startDate.slice(0, 10) <= rangeEndIso && task.endDate.slice(0, 10) >= rangeStartIso)),
  }));

  const rows = buildCompiledRows(dateFilteredGroups);
  const rowLayout = computeCompiledRowLayout(rows);
  const bars = computeCompiledBars(rows, rangeStart);
  const depthById = computeDepthById(dateFilteredGroups.flatMap((g) => g.tasks));

  const today = new Date();
  const todayUtc = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  const todayOffset = diffDaysUTC(rangeStart, todayUtc);
  const todayDayIndex = todayOffset >= 0 && todayOffset < totalDays ? todayOffset : null;

  const plan = computeCompiledPagePlan({
    rowLayout,
    dayCount: totalDays,
    paperSize: settings.paperSize,
    orientation: settings.orientation,
    marginCm: settings.marginCm,
    scalePercent: settings.scalePercent,
  });

  const orientedPaper = orientedPaperSizeMm(settings.paperSize, settings.orientation);
  const doc = new jsPDF({ unit: 'mm', format: [orientedPaper.w, orientedPaper.h], orientation: settings.orientation });
  const scale = settings.scalePercent / 100;

  const allLogos = await api.get<Logo[]>('/logos');
  const logosById = new Map(allLogos.map((logo) => [logo.id, logo]));
  const logos = settings.logoIds.map((id) => logosById.get(id)).filter((l): l is Logo => !!l);

  const contentY0 = plan.marginMm + PAGE_HEADER_BAND_MM + PAGE_HEADER_GAP_MM;

  let firstPage = true;
  for (let pageRowIdx = 0; pageRowIdx < plan.rowPages.length; pageRowIdx++) {
    const { startIndex: rowStart, endIndex: rowEnd } = plan.rowPages[pageRowIdx];

    for (let pageCol = 0; pageCol < plan.pagesWide; pageCol++) {
      if (!firstPage) doc.addPage([orientedPaper.w, orientedPaper.h], settings.orientation);
      firstPage = false;

      const includeLabelCol = pageCol === 0;
      const dayStart = pageCol === 0 ? 0 : plan.daysOnFirstCol + (pageCol - 1) * plan.daysPerLaterCol;
      const dayCapacity = pageCol === 0 ? plan.daysOnFirstCol : plan.daysPerLaterCol;
      const dayCount = Math.max(0, Math.min(dayCapacity, totalDays - dayStart));

      const headerSvg =
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${plan.usableWmm} ${PAGE_HEADER_BAND_MM}" width="${plan.usableWmm}" height="${PAGE_HEADER_BAND_MM}">` +
        `<text x="1" y="${PAGE_HEADER_BAND_MM / 2 + 2}" font-size="5" font-family="Helvetica, Arial, sans-serif" font-weight="bold" fill="#0f172a">${escapeXml(title)}</text>` +
        `<line x1="0" y1="${PAGE_HEADER_BAND_MM - 0.5}" x2="${plan.usableWmm}" y2="${PAGE_HEADER_BAND_MM - 0.5}" stroke="#0f172a" stroke-width="0.5"/>` +
        `</svg>`;
      await renderSvgString(doc, headerSvg, { x: plan.marginMm, y: plan.marginMm, width: plan.usableWmm, height: PAGE_HEADER_BAND_MM });

      if (logos.length > 0) {
        const gapMm = 3;
        const totalLogosWidth = logos.length * LOGO_SIZE_MM + (logos.length - 1) * gapMm;
        const startX = plan.marginMm + (plan.usableWmm - totalLogosWidth) / 2;
        const logoY = plan.marginMm + (PAGE_HEADER_BAND_MM - LOGO_SIZE_MM) / 2;
        for (let i = 0; i < logos.length; i++) {
          const logo = logos[i];
          const x = startX + i * (LOGO_SIZE_MM + gapMm);
          const vectorMarkup = buildLogoSvgMarkup(logo.svgContent, logo.cropX, logo.cropY, logo.zoom, 100, `logo${i}_`);
          let drew = false;
          if (vectorMarkup) {
            try {
              await renderSvgString(doc, vectorMarkup, { x, y: logoY, width: LOGO_SIZE_MM, height: LOGO_SIZE_MM });
              drew = true;
            } catch {
              drew = false;
            }
          }
          if (!drew) {
            try {
              const png = await rasterizeLogoToPngDataUri(logo.svgContent, logo.cropX, logo.cropY, logo.zoom, 96);
              doc.addImage(png, 'PNG', x, logoY, LOGO_SIZE_MM, LOGO_SIZE_MM);
            } catch {
              // Logo ni vectorisable ni rastérisable : on continue sans lui plutôt que d'interrompre tout l'export.
            }
          }
        }
      }

      if (dayCount <= 0 && !includeLabelCol) continue;

      const { svg, widthPx, heightPx } = buildCompiledPageContentSvg({
        rows,
        rowLayoutFull: rowLayout,
        bars,
        depthById,
        includeLabelCol,
        dateFormat,
        weekNumbering,
        rangeStart,
        dayStart,
        dayCount,
        rowStart,
        rowEnd,
        todayDayIndex,
        weekAbbrev,
        columnLabel,
        locale,
      });

      const widthMm = widthPx * PX_TO_MM * scale;
      const heightMm = heightPx * PX_TO_MM * scale;
      await renderSvgString(doc, svg, { x: plan.marginMm, y: contentY0, width: widthMm, height: heightMm });
    }
  }

  doc.save(`${sanitizeFilename(title)}.pdf`);
}
